import { useRef, type ChangeEvent } from 'react'
import { ImageMinus, ImagePlus } from 'lucide-react'
import { NodeKind, collageCellContext } from '@/core/document'
import { IMAGE_FILE_ACCEPT } from '@/core/image'
import { ReportingStage, surfaceError } from '@/core/reporting'
import { useEditSession } from '@/core/store/editSession'
import { selectSelectedNode } from '@/core/store/selectors'
import { TEST_IDS } from '@/lib/testIds'
import {
  removeSelectedCollageCell,
  replaceSelectedCollageImage,
} from '@/tools/collage/actions'
import { Button } from '@/ui/shadcn/button'

export function CollageCellPanel() {
  const inputRef = useRef<HTMLInputElement>(null)
  const document = useEditSession((state) => state.document)
  const selected = useEditSession(selectSelectedNode)
  if (!document) throw new Error('Collage cell panel requires a loaded document')
  if (selected.kind !== NodeKind.Image || !collageCellContext(document.root, selected.id)) {
    throw new Error('Collage cell panel requires a selected Image node inside a collage cell')
  }

  const chooseReplacement = () => {
    const input = inputRef.current
    if (!input) throw new Error('Collage replacement file picker is not configured')
    input.click()
  }

  const replaceImage = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0]
    event.currentTarget.value = ''
    if (!file) return
    void replaceSelectedCollageImage(file).catch((error: unknown) => {
      surfaceError(error, 'Could not replace the collage photo.', { stage: ReportingStage.SourceLoad })
    })
  }

  return (
    <section className="space-y-3" aria-labelledby="collage-cell-heading">
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        accept={IMAGE_FILE_ACCEPT}
        onChange={replaceImage}
        aria-label="Replace collage photo"
      />
      <h2 id="collage-cell-heading" className="font-semibold">Collage cell</h2>
      <div className="grid grid-cols-2 gap-2">
        <Button type="button" variant="outline" data-testid={TEST_IDS.collageCellReplace} onClick={chooseReplacement}><ImagePlus aria-hidden="true" />Replace</Button>
        <Button type="button" variant="outline" data-testid={TEST_IDS.collageCellRemove} onClick={removeSelectedCollageCell}><ImageMinus aria-hidden="true" />Remove photo</Button>
      </div>
    </section>
  )
}
